import { Document, Page, Text, View, Image, Link } from "@react-pdf/renderer";
import React from "react";
import lnnlogo from "assets/incidentReports/lnnlogo.png";
import CheckpointResponseTypes from "containers/pages/checklists/constants/checkpointResponseTypes";
import { IncidentReportStyles as styles } from "./IncidentReportStyles";
import videoIcon from "assets/incidentReports/playButtonImage.png";
import dayjs from "dayjs";
import { previewFile } from "api";

const SpacedApartLine = ({ line }) => {
  const { left, right } = line;
  return (
    <View style={styles.spacedApartLineWrapper}>
      <View style={styles.spacedApartLineTextWrapper}>
        <Text style={{ color: "#ab131d" }}>{left.redText}</Text>
        <Text>{left.blackText}</Text>
      </View>
      <View style={styles.spacedApartLineTextWrapper}>
        <Text style={{ color: "#ab131d" }}>{right.redText}</Text>
        <Text>{right.blackText}</Text>
      </View>
    </View>
  );
};

const CheckpointContentSignature = ({ attachments, incidentDate }) => {
  return (
    <>
      <View style={styles.checkpointImagesWrapper}>
        {attachments
          .filter((attachment) => attachment.ContentType.includes("image"))
          .map((attachment) => (
            <View
              style={styles.checkpointSignatureContainer}
              key={attachment.AttachmentID}
            >
              <View style={styles.checkpointSignatureWrapper}>
                <Text>Signature:</Text>
                <Image
                  src={previewFile(attachment.AttachmentID)}
                  style={styles.checkpointSignatureImage}
                />
              </View>
              <View
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Text>Date: {incidentDate}</Text>
              </View>
            </View>
          ))}
      </View>
    </>
  );
};

const CheckpointContentAttachment = ({ attachments }) => {
  const isImage = (contentType) => contentType.includes("image");
  const isVideo = (contentType) => contentType.includes("video");

  const otherAttachments = attachments.filter(
    (attachment) =>
      !isImage(attachment.ContentType) && !isVideo(attachment.ContentType)
  );

  return (
    <>
      <View style={styles.checkpointImagesWrapper}>
        {attachments.map((attachment) => {
          const isAttachmentVideo = isVideo(attachment.ContentType);
          if (isImage(attachment.ContentType) || isAttachmentVideo) {
            return (
              <Link
                src={`${window.location.origin}/api/fileuploadservice/Preview?FileId=${attachment.AttachmentID}`}
                key={attachment.AttachmentID}
              >
                <View style={styles.checkpointImageWrapper}>
                  <Image
                    src={
                      isAttachmentVideo
                        ? videoIcon
                        : previewFile(attachment.AttachmentID)
                    }
                    style={styles.checkpointImage}
                  />
                  <Text>{attachment.Name}</Text>
                </View>
              </Link>
            );
          } else {
            return null;
          }
        })}
      </View>
      {/* Files content: Add links with a text: */}
      <View style={styles.checkpointNonImageAttachmentsWrapper}>
        {otherAttachments.length > 0 && <Text>Other attachments:</Text>}
        {otherAttachments.map((attachment) => (
          <Text
            style={styles.checkpointNonImageAttachmentsText}
            key={attachment.AttachmentID}
          >
            <Link
              src={`${window.location.origin}/api/fileuploadservice/Preview?FileId=${attachment.AttachmentID}`}
            >
              {attachment.Name}
            </Link>
          </Text>
        ))}
      </View>
    </>
  );
};
const CheckpointContent = ({ checkpoint, incidentDate }) => {
  const parseDotNetDate = (dotNetString) => {
    const match = /\/Date\((\d+)\)\//.exec(dotNetString);
    return match ? new Date(parseInt(match[1], 10)) : null;
  };

  const isStatus =
    checkpoint.ResponseTypeName === CheckpointResponseTypes.Status;
  const isComment =
    checkpoint.ResponseTypeName === CheckpointResponseTypes.Comment;
  const isTime = checkpoint.ResponseTypeName === CheckpointResponseTypes.Time;
  const isAttachment =
    checkpoint.ResponseTypeName === CheckpointResponseTypes.Attachment;
  const isSignature =
    checkpoint.ResponseTypeName === CheckpointResponseTypes.Signature;
  const isDropdown =
    checkpoint.ResponseTypeName === CheckpointResponseTypes.Dropdown;
  const isDropdownMultiSelect =
    checkpoint.ResponseTypeName === CheckpointResponseTypes.DropdownMultiSelect;
  const isDateTime =
    checkpoint.ResponseTypeName === CheckpointResponseTypes.DateTime;

  const getDropdownText = () => {
    const dropdownItems = checkpoint.Dropdowns;
    const selectedIDs = checkpoint.SelectedDropdownIDs;
    const dropdownNames = dropdownItems
      .filter((dropdown) => selectedIDs.includes(dropdown.DropdownID))
      .map((dropdown) => dropdown.Name);
    return dropdownNames?.join(", ");
  };

  const getStatusText = () => {
    const statusItems = checkpoint.Status;
    const selectedID = checkpoint.SelectedStatusID;
    const status = statusItems.find((status) => status.StatusID === selectedID);
    return status?.Name || "incomplete";
  };

  return (
    <>
      {isTime && <Text>{checkpoint.Time}</Text>}
      {isDateTime && (
        <Text>
          {checkpoint.CheckpointDateTime
            ? dayjs(parseDotNetDate(checkpoint.CheckpointDateTime)).format(
                "YYYY-MM-DD HH:mm:ss"
              )
            : "Incomplete"}
        </Text>
      )}
      {isStatus && <Text>{getStatusText()}</Text>}{" "}
      {(isComment || (isStatus && checkpoint.comment)) && (
        <Text>{checkpoint.Comment}</Text>
      )}
      {(isDropdown || isDropdownMultiSelect) && (
        <Text>{getDropdownText()}</Text>
      )}
      {isSignature && (
        <CheckpointContentSignature
          attachments={checkpoint.Attachments}
          incidentDate={incidentDate}
        />
      )}
      {isAttachment && (
        <CheckpointContentAttachment attachments={checkpoint.Attachments} />
      )}
    </>
  );
};

export const IncidentReport = ({ incident, documentName }) => {
  const incidentDate = dayjs(incident.StartTime).format("YYYY-MM-DD");
  const lines = [
    {
      left: {
        redText: "Reported by: ",
        blackText: incident.GuardName,
      },
      right: {
        redText: "Date of report: ",
        blackText: `${dayjs().format("YYYY-MM-DD")}`,
      },
    },
    {
      left: { redText: "Title: ", blackText: "Security Professional" },
      right: {
        redText: "Incident Number: ",
        blackText: incident.CommentListID,
      },
    },
    {
      left: {
        redText: "Customer: ",
        blackText: incident.Customer,
      },
      right: {
        redText: "Address: ",
        blackText: incident.Address,
      },
    },
    {
      left: { redText: "Date of incident: ", blackText: incidentDate },
      right: {
        redText: "",
        blackText: ``,
      },
    },
  ];
  return (
    <Document title={documentName}>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.headerWrapper}>
          <Text style={styles.headerText}>
            {incident.IncidentChecklist.Name} Incident Report
          </Text>
          <Image src={lnnlogo} style={styles.logo} />
        </View>
        <SpacedApartLine line={lines[0]} />
        <SpacedApartLine line={lines[1]} />
        <View style={styles.redContainer}>
          <Text style={styles.redContainerText}>Incident Information</Text>
        </View>
        <SpacedApartLine line={lines[2]} />
        <SpacedApartLine line={lines[3]} />

        <View>
          {incident.IncidentChecklist.Checkpoints?.map((checkpoint, index) => (
            <View style={styles.checkpointContainer} key={index}>
              <Text style={styles.checkpointName}>{checkpoint.Name}</Text>
              <CheckpointContent
                checkpoint={checkpoint}
                incidentDate={incidentDate}
              />
            </View>
          ))}
        </View>

        <Text
          style={styles.pageFooterRight}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} of ${totalPages}`
          }
          fixed
        />
        <Text style={styles.pageFooterLeft} fixed>
          {`Incident Report by ${incident.GuardName} on ${incidentDate}`}
        </Text>
      </Page>
    </Document>
  );
};
